import * as React from 'react';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';
import { isReactRenderable, omit } from '@rc-component/util';
import { clsx } from 'clsx';
import fallbackProp from '../_util/fallbackProp';
import { CONTAINER_MAX_OFFSET, normalizeMaskConfig } from '../_util/hooks';
import { isFunction, isPlainObject } from '../_util/is';
import { getTransitionName } from '../_util/motion';
import { devUseWarning } from '../_util/warning';
import ConfigProvider from '../config-provider';
import { useComponentConfig } from '../config-provider/context';
import { useLocale } from '../locale';
import useToken from '../theme/useToken';
import CancelBtn from './components/ConfirmCancelBtn';
import OkBtn from './components/ConfirmOkBtn';
import { ModalContextProvider } from './context';
import Modal from './Modal';
import Confirm from './style/confirm';
const CONFIRM_OMIT_SEMANTIC_NAMES = ['body'];
export const ConfirmContent = (props) => {
    const { prefixCls, icon, okText, cancelText, confirmPrefixCls, type, okCancel, footer, 
    // Legacy for static function usage
    locale: staticLocale, autoFocusButton, focusable, contentClassName, contentStyle, ...restProps } = props;
    const { infoIcon, successIcon, errorIcon, warningIcon } = useComponentConfig('modal');
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Modal');
        warning(!(typeof icon === 'string' && icon.length > 2), 'breaking', `\`icon\` is using ReactNode instead of string naming in v4. Please check \`${icon}\` at https://ant.design/components/icon`);
    }
    // Icon
    let mergedIcon = icon;
    // 支持传入 { icon: null } 或 { icon: false } 来隐藏`Modal.confirm`默认的Icon
    if (icon === undefined) {
        switch (type) {
            case 'info':
                mergedIcon = fallbackProp(infoIcon, React.createElement(InfoCircleFilled, null));
                break;
            case 'success':
                mergedIcon = fallbackProp(successIcon, React.createElement(CheckCircleFilled, null));
                break;
            case 'error':
                mergedIcon = fallbackProp(errorIcon, React.createElement(CloseCircleFilled, null));
                break;
            default:
                mergedIcon = fallbackProp(warningIcon, React.createElement(ExclamationCircleFilled, null));
        }
    }
    // 默认为 true，保持向下兼容
    const mergedOkCancel = okCancel ?? type === 'confirm';
    const mergedAutoFocusButton = React.useMemo(() => {
        const base = focusable?.autoFocusButton || autoFocusButton;
        return base || base === null ? base : 'ok';
    }, [autoFocusButton, focusable?.autoFocusButton]);
    const [locale] = useLocale('Modal');
    const mergedLocale = staticLocale || locale;
    // ================== Locale Text ==================
    const okTextLocale = fallbackProp(okText, mergedOkCancel ? mergedLocale?.okText : mergedLocale?.justOkText);
    const cancelTextLocale = fallbackProp(cancelText, mergedLocale?.cancelText);
    // ================= Context Value =================
    const { closable } = restProps;
    const { onClose } = isPlainObject(closable) ? closable : {};
    const memoizedValue = React.useMemo(() => {
        return {
            autoFocusButton: mergedAutoFocusButton,
            cancelTextLocale,
            okTextLocale,
            mergedOkCancel,
            onClose,
            ...restProps,
        };
    }, [mergedAutoFocusButton, cancelTextLocale, okTextLocale, mergedOkCancel, onClose, restProps]);
    // ====================== Footer Origin Node ======================
    const footerOriginNode = (React.createElement(React.Fragment, null,
        React.createElement(CancelBtn, null),
        React.createElement(OkBtn, null)));
    const hasTitle = isReactRenderable(props.title);
    const hasIcon = isReactRenderable(mergedIcon);
    const bodyCls = `${confirmPrefixCls}-body`;
    return (React.createElement("div", { className: `${confirmPrefixCls}-body-wrapper` },
        React.createElement("div", { className: clsx(bodyCls, {
                [`${bodyCls}-has-title`]: hasTitle,
                [`${bodyCls}-no-icon`]: !hasIcon,
            }) },
            mergedIcon,
            React.createElement("div", { className: `${confirmPrefixCls}-paragraph` },
                hasTitle && React.createElement("span", { className: `${confirmPrefixCls}-title` }, props.title),
                React.createElement("div", { className: clsx(`${confirmPrefixCls}-content`, contentClassName), style: contentStyle }, props.content))),
        footer === undefined || isFunction(footer) ? (React.createElement(ModalContextProvider, { value: memoizedValue },
            React.createElement("div", { className: `${confirmPrefixCls}-btns` }, isFunction(footer) ? footer(footerOriginNode, { OkBtn, CancelBtn }) : footerOriginNode))) : (footer),
        React.createElement(Confirm, { prefixCls: prefixCls })));
};
const ConfirmDialog = (props) => {
    const { close, zIndex, maskStyle, direction, prefixCls, wrapClassName, rootPrefixCls, bodyStyle, closable = false, onConfirm, styles, title, mask, maskClosable, okButtonProps, cancelButtonProps, } = props;
    const { cancelButtonProps: contextCancelButtonProps, okButtonProps: contextOkButtonProps } = useComponentConfig('modal');
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Modal');
        [
            ['bodyStyle', 'styles.body'],
            ['maskStyle', 'styles.mask'],
        ].forEach(([deprecatedName, newName]) => {
            warning.deprecated(!(deprecatedName in props), deprecatedName, newName);
        });
    }
    const confirmPrefixCls = `${prefixCls}-confirm`;
    const width = props.width || 416;
    const style = props.style || {};
    const semanticStyles = isFunction(styles)
        ? (info) => ({ body: bodyStyle, mask: maskStyle, ...styles(info) })
        : { body: bodyStyle, mask: maskStyle, ...styles };
    const modalProps = omit(props, ['bodyStyle', 'maskStyle']);
    const classString = clsx(confirmPrefixCls, `${confirmPrefixCls}-${props.type}`, { [`${confirmPrefixCls}-rtl`]: direction === 'rtl' }, props.className);
    // ========================== Mask ==========================
    // 默认为 false，保持旧版默认行为
    const mergedMask = React.useMemo(() => {
        const nextMaskConfig = normalizeMaskConfig(mask, maskClosable);
        nextMaskConfig.closable ?? (nextMaskConfig.closable = false);
        return nextMaskConfig;
    }, [mask, maskClosable]);
    // ========================= zIndex =========================
    const [, token] = useToken();
    const mergedZIndex = React.useMemo(() => {
        if (zIndex !== undefined) {
            return zIndex;
        }
        // Static always use max zIndex
        return token.zIndexPopupBase + CONTAINER_MAX_OFFSET;
    }, [zIndex, token]);
    // ========================= Render =========================
    return (React.createElement(Modal, { ...modalProps, className: classString, wrapClassName: clsx({ [`${confirmPrefixCls}-centered`]: !!props.centered }, wrapClassName), onCancel: () => {
            close?.({ triggerCancel: true });
            onConfirm?.(false);
        }, title: title, footer: null, transitionName: getTransitionName(rootPrefixCls || '', 'zoom', props.transitionName), maskTransitionName: getTransitionName(rootPrefixCls || '', 'fade', props.maskTransitionName), mask: mergedMask, style: style, styles: semanticStyles, width: width, zIndex: mergedZIndex, closable: closable, _semanticOmit: CONFIRM_OMIT_SEMANTIC_NAMES,
        _renderSemanticContent: ({ classNames: mergedClassNames, styles: mergedStyles, }) => (React.createElement(ConfirmContent, { ...props, confirmPrefixCls: confirmPrefixCls, okButtonProps: { ...contextOkButtonProps, ...okButtonProps }, cancelButtonProps: { ...contextCancelButtonProps, ...cancelButtonProps }, contentClassName: mergedClassNames.body, contentStyle: mergedStyles.body })) }));
};
const ConfirmDialogWrapper = (props) => {
    const { rootPrefixCls, iconPrefixCls, direction, theme } = props;
    return (React.createElement(ConfigProvider, { prefixCls: rootPrefixCls, iconPrefixCls: iconPrefixCls, direction: direction, theme: theme },
        React.createElement(ConfirmDialog, { ...props })));
};
if (process.env.NODE_ENV !== 'production') {
    ConfirmDialog.displayName = 'ConfirmDialog';
    ConfirmDialogWrapper.displayName = 'ConfirmDialogWrapper';
}
export default ConfirmDialogWrapper;
